<!--
  COD3 · Riepilogo booking — resoconto mensile di un partner.

  COS'È: il resoconto di tutte le prenotazioni effettuate, per vedere quanto è
  stato fatturato in ogni mese. **Non ha una tabella propria**: è una vista sui
  booking stessi —
    periodo   → `booking.data`
    fatturato → `booking.tot_fattura` (base imponibile in `imponibile`)
    stato     → `booking.liquidato` + `rif_fattura`
    partner   → `booking.partnerId`

  L'id della rotta è quello del **partner**, non della sede: il resoconto è
  dell'intero partner con tutte le sue sedi. Con l'id `tutti` la pagina
  mostra i ricavi di TUTTI i partner del mese, con la colonna Partner; da lì
  non si fattura (una fattura è di un partner solo).

  Mostra anche gli SPOSTAMENTI INTERNI del mese, una riga per giornata, con un
  filtro per tipo di servizio. Si fatturano da qui come le prenotazioni
  (`journey.liquidato` + `rif_fattura`, sulla giornata). L'imponibile è la
  somma di `route.applied_price`; il totale con IVA si calcola qui (il server
  non lo manda con l'elenco). La riga della giornata si apre sulle sue corse
  (orario, percorso, veicolo, driver e jolly, singolo o a catena, prezzo):
  singolo / a catena è della corsa, non della giornata.

  I NOTTURNI si riconoscono da `type_booking = 'restaurant'`. Per ogni
  prenotazione si vede anche quanto il cliente ha già pagato IN APP.

  IL FATTURATO DEL MESE comprende prenotazioni E spostamenti interni, e così
  «Da fatturare» e «già fatturato».

  LA BENZINA fatta al veicolo del partner è una voce a sé (`carburante`, sulla
  prenotazione o sulla singola corsa di uno spostamento): rimborso spese
  senza IVA sopra, fuori dall'imponibile. Entra nel Totale da fatturare della
  riga e ha il suo KPI; il partner la vede.

  IL FATTURATO È L'IMPONIBILE: i totali di questa pagina (KPI, riga dei totali,
  PDF) sono sempre al netto dell'IVA. Se il cliente ha pagato IN APP, quello
  che ha pagato comprende l'IVA: si riporta al netto (÷ 1,22) e sostituisce
  l'imponibile della prenotazione, perché l'incasso è quello.

  Funzioni:
   1. **fatturazione**: si selezionano le prenotazioni, si indica un
      riferimento fattura e si segnano come fatturate
      (`rif_fattura` + `liquidato: true` su ciascuna);
   2. **esportazione PDF** delle righe selezionate, in orizzontale: una riga
      per corsa (orario, partenza e arrivo, veicolo, km, minuti), con data,
      servizio e importi della prenotazione o giornata uniti sulle sue corse;
      in fondo i totali.
-->
<template>
  <div
    v-loading="loading"
    class="c3"
  >
    <div class="c3__head">
      <button
        type="button"
        class="c3__back"
        @click="goBack"
      >
        ‹
      </button>
      <div>
        <h1 class="c3__title">
          <span class="c3__code">COD3</span> Riepilogo booking
        </h1>
        <p class="c3__sub">
          {{ tutti ? 'Tutti i partner' : (partnerName || 'Partner') }} — prenotazioni del mese e fatturato
        </p>
      </div>
    </div>

    <div class="c3__filters">
      <div class="field">
        <label>Mese</label>
        <div class="period">
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(-1)"
          >
            ‹
          </button>
          <span class="period__lbl">{{ periodLabel }}</span>
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(1)"
          >
            ›
          </button>
        </div>
      </div>
      <div class="field">
        <label>Servizio</label>
        <el-select
          v-model="servizio"
          style="width: 210px"
        >
          <el-option
            v-for="s in serviziDisponibili"
            :key="s.value"
            :label="s.label"
            :value="s.value"
          />
        </el-select>
      </div>
      <div class="c3__sp" />
      <el-input
        v-model="search"
        :placeholder="tutti ? 'Partner, cliente o targa…' : 'Cliente o targa…'"
        clearable
        style="width: 240px"
      />
    </div>

    <div
      v-if="truncated"
      class="warnbox"
    >
      <span class="warnbox__ico">⚠</span>
      <div class="warnbox__txt">
        <b>Elenco incompleto.</b>
        Il backend ha restituito {{ bookings.length }} prenotazioni su
        {{ declaredTotal }} dichiarate: i totali sarebbero parziali.
      </div>
    </div>

    <div class="kpis">
      <div class="kpi">
        <div class="kpi__lbl">
          Fatturato del mese
          <small>imponibile</small>
        </div>
        <div class="kpi__num">
          {{ truncated ? '—' : euro(kpi.total) }}
        </div>
      </div>
      <div class="kpi kpi--attn">
        <div class="kpi__lbl">
          Da fatturare
        </div>
        <div class="kpi__num">
          {{ truncated ? '—' : euro(kpi.toInvoice) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Già fatturato
        </div>
        <div class="kpi__num">
          {{ truncated ? '—' : euro(kpi.invoiced) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Pagato in app
        </div>
        <div class="kpi__num">
          {{ truncated ? '—' : euro(kpi.inApp) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Spostamenti interni
          <small>compresi nel fatturato</small>
        </div>
        <div class="kpi__num">
          {{ euro(kpi.spostamenti) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Benzina
          <small>rimborsi, fuori dal fatturato</small>
        </div>
        <div class="kpi__num">
          {{ truncated ? '—' : euro(kpi.benzina) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Righe
        </div>
        <div class="kpi__num">
          {{ bookings.length + journeys.length }}
        </div>
      </div>
    </div>

    <div class="board">
      <div class="toolbar">
        <div class="segs">
          <button
            v-for="s in SEGMENTS"
            :key="s.key"
            type="button"
            class="seg"
            :class="{ 'is-active': segment === s.key }"
            @click="setSegment(s.key)"
          >
            {{ s.label }}
            <span class="seg__c">{{ countOf(s.key) }}</span>
          </button>
        </div>
      </div>

      <el-table
        ref="tableRef"
        :data="filtered"
        style="width: 100%"
        row-key="id"
        :row-class-name="({ row }) => (row.__journey ? '' : 'c3-row--noexp')"
        :empty-text="segment === 'dafatt' && countOf('fatt')
          ? `Niente da fatturare: ${countOf('fatt')} ${countOf('fatt') === 1 ? 'riga già fatturata' : 'righe già fatturate'}, nella scheda Fatturate`
          : 'Niente per il periodo selezionato'"
        @selection-change="onSelectionChange"
      >
        <!-- Prenotazioni e spostamenti interni si fatturano insieme. -->
        <el-table-column
          v-if="segment === 'dafatt'"
          type="selection"
          width="46"
        />

        <!-- La giornata di uno spostamento si apre sulle sue corse. Le
             prenotazioni non hanno niente da aprire: niente freccia. -->
        <el-table-column
          type="expand"
          width="36"
        >
          <template #default="scope">
            <div
              v-if="scope.row.__journey"
              class="c3x"
            >
              <!-- Un elenco, non una tabella annidata: una corsa per riga. -->
              <ul class="c3x__list">
                <li
                  v-for="c in corseDi(scope.row)"
                  :key="c.id"
                  class="c3x__corsa"
                >
                  <div class="c3x__ora num">
                    {{ c.orario || '—' }}
                  </div>
                  <div class="c3x__main">
                    <div class="c3x__percorso">
                      {{ c.da || '—' }} <span class="muted">→</span> {{ c.a || '—' }}
                    </div>
                    <div class="cli__sub">
                      {{ [c.veicolo, c.targa].filter(Boolean).join(' · ') || 'Veicolo —' }}
                      <span
                        v-if="c.prova"
                        class="c3__tagImm"
                        title="Veicolo non immatricolato: si sposta con la targa prova"
                      >Targa prova</span>
                    </div>
                    <!-- Stato, km e minuti della corsa, sotto il veicolo. -->
                    <div class="cli__sub">
                      {{ [c.stato, c.km !== null ? `${c.km} km` : '', c.min !== null ? `${c.min} min` : ''].filter(Boolean).join(' · ') }}
                    </div>
                  </div>
                  <!-- Il jolly c'è solo sul singolo: a catena il driver guida
                       da solo. -->
                  <div class="c3x__chi">
                    <div>
                      <span class="cod3__tipo">{{ c.tipo }}</span>
                      {{ c.driver || 'Driver —' }}
                    </div>
                    <div
                      v-if="c.jolly"
                      class="cli__sub"
                    >
                      {{ c.jolly }}
                    </div>
                  </div>
                  <!-- Gli importi della corsa in colonna: voce, importo e
                       azione, poi il totale. Una corsa ancora da confermare
                       ha i suoi importi ma non entra nel totale della
                       giornata. -->
                  <div
                    class="c3x__importi"
                    :class="{ 'is-fuori': !c.nelTotale }"
                  >
                    <span class="c3x__voce">
                      Prezzo<small
                        v-if="c.aMano"
                        class="c3__manuale"
                      > a mano</small>
                    </span>
                    <span class="num">{{ c.prezzo === null ? '—' : euro(c.prezzo) }}</span>
                    <button
                      type="button"
                      class="c3__edit"
                      :aria-label="`Modifica il prezzo della corsa delle ${c.orario}`"
                      @click="apriImportoCorsa(scope.row, c)"
                    >
                      Modifica
                    </button>

                    <span class="c3x__voce">Benzina</span>
                    <span class="num">{{ c.benzina ? euro(c.benzina) : '—' }}</span>
                    <button
                      type="button"
                      class="c3__edit"
                      :aria-label="`Benzina della corsa delle ${c.orario}`"
                      @click="apriBenzina(scope.row, c)"
                    >
                      {{ c.benzina ? 'Modifica' : 'Aggiungi' }}
                    </button>

                    <span class="c3x__voce c3x__voce--tot">Totale</span>
                    <span class="num c3x__tot">{{ euro(Number(c.prezzo || 0) + Number(c.benzina || 0)) }}</span>
                    <span />
                    <span
                      v-if="!c.nelTotale"
                      class="c3x__fuori"
                    >fuori dal totale: corsa da confermare</span>
                  </div>
                </li>
              </ul>
              <!-- I totali della giornata, sotto la colonna degli importi. -->
              <div class="c3x__foot">
                <span class="c3x__voce">Imponibile corse</span>
                <b class="num">{{ euro(scope.row.imponibile) }}</b>
                <template v-if="benzinaDi(scope.row)">
                  <span class="c3x__voce">Benzina</span>
                  <b class="num">{{ euro(benzinaDi(scope.row)) }}</b>
                </template>
                <p
                  v-if="aMano(scope.row)"
                  class="c3x__nota"
                >
                  La giornata è corretta a mano: si fattura {{ euro(fatturato(scope.row)) }} imponibile.
                </p>
              </div>
            </div>
          </template>
        </el-table-column>

        <!-- Tutti i partner: di chi è la riga. -->
        <el-table-column
          v-if="tutti"
          label="Partner"
          min-width="170"
        >
          <template #default="scope">
            <span class="cli__nm c3__partner">{{ partnerDi(scope.row) || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Data"
          width="120"
          sortable
          :sort-method="(a, z) => new Date(a.data || 0) - new Date(z.data || 0)"
        >
          <template #default="scope">
            {{ fmtDate(scope.row.data) }}
            <!-- L'ora c'è solo sulle prenotazioni: uno spostamento interno è
                 una GIORNATA, non un orario. -->
            <div
              v-if="fmtTime(scope.row)"
              class="cli__sub"
            >
              {{ fmtTime(scope.row) }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Cliente"
          min-width="190"
        >
          <template #default="scope">
            <div class="cli__nm">
              {{ customerName(scope.row) }}
            </div>
            <div class="cli__sub">
              {{ scope.row.__journey
                ? `${scope.row.tratte_contabili} ${scope.row.tratte_contabili === 1 ? 'tratta' : 'tratte'}`
                : (scope.row.headquarter?.name || scope.row.headquarter?.city || '') }}
            </div>
            <!-- L'auto: sulla prenotazione sotto il cliente; sullo spostamento
                 è di ogni corsa, e sta aprendo la giornata. Qui resta solo il
                 segno della targa prova (quante corse la usano). -->
            <div
              v-if="!scope.row.__journey && autoDi(scope.row)"
              class="cli__sub c3__auto"
            >
              {{ autoDi(scope.row).label }}<template v-if="autoDi(scope.row).plate">
                · <span class="c3__targa">{{ autoDi(scope.row).plate }}</span>
              </template>
            </div>
            <span
              v-if="scope.row.__journey && autoDi(scope.row)?.prova"
              class="c3__tagImm"
              title="Veicolo non immatricolato: si sposta con la targa prova"
            >{{ autoDi(scope.row).prova }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Servizio"
          min-width="220"
        >
          <template #default="scope">
            <ServiceChip :service="servizioDi(scope.row)" />
          </template>
        </el-table-column>

        <el-table-column
          label="Imponibile"
          width="130"
          align="right"
        >
          <template #default="scope">
            <span class="num">{{ euro(fatturato(scope.row)) }}</span>
            <!-- Corretto a mano da Simba; altrimenti, pagato in app: si
                 fattura quell'incasso al netto dell'IVA, non l'imponibile con
                 cui era nata la prenotazione. -->
            <div
              v-if="aMano(scope.row)"
              class="cli__sub c3__manuale"
            >
              a mano
            </div>
            <div
              v-else-if="!scope.row.__journey && fatturato(scope.row) !== Number(scope.row.imponibile || 0)"
              class="cli__sub"
            >
              da app
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Benzina"
          width="130"
          align="right"
        >
          <!-- Rimborso spese a parte, senza IVA. Sugli spostamenti è la
               somma delle corse (si scrive aprendo la giornata). -->
          <template #default="scope">
            <span
              v-if="benzinaDi(scope.row)"
              class="num"
            >{{ euro(benzinaDi(scope.row)) }}</span>
            <span
              v-else
              class="muted"
            >—</span>
            <button
              v-if="!scope.row.__journey"
              type="button"
              class="c3__edit c3__edit--block"
              :aria-label="`Benzina della prenotazione di ${customerName(scope.row)}`"
              @click="apriBenzina(scope.row, null)"
            >
              {{ benzinaDi(scope.row) ? 'Modifica' : '+ Aggiungi' }}
            </button>
          </template>
        </el-table-column>

        <el-table-column
          label="Totale"
          width="130"
          align="right"
        >
          <!-- Da fatturare: il servizio con IVA più la benzina. -->
          <template #default="scope">
            <span class="num val">{{ euro(daFatturareDi(scope.row)) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Pagato in app"
          width="140"
          align="right"
        >
          <template #default="scope">
            <span
              v-if="scope.row.__journey"
              class="muted"
            >—</span>
            <span
              v-else-if="pagatoInApp(scope.row)"
              class="num val"
            >{{ euro(pagatoInApp(scope.row)) }}</span>
            <span
              v-else
              class="muted"
            >—</span>
            <div
              v-if="!scope.row.__journey && daPagareInApp(scope.row)"
              class="cli__sub"
            >{{ euro(daPagareInApp(scope.row)) }} da pagare</div>
          </template>
        </el-table-column>

        <!-- Correggere imponibile e totale a mano (sconti, accordi). -->
        <el-table-column
          width="100"
          align="center"
        >
          <template #default="scope">
            <button
              type="button"
              class="c3__edit"
              :aria-label="`Modifica gli importi di ${customerName(scope.row)}`"
              title="Modifica gli importi"
              @click="apriImporti(scope.row)"
            >
              Modifica
            </button>
          </template>
        </el-table-column>

        <el-table-column
          label="Stato"
          min-width="190"
        >
          <template #default="scope">
            <!-- La pill resta corta: non entrava nella colonna e veniva
                 tagliata. La spiegazione sta sotto, dove può andare a capo. -->
            <StatusPill
              :kind="scope.row.liquidato ? 'ok' : 'attn'"
              :label="scope.row.liquidato ? 'Fatturato' : 'Da fatturare'"
            />
            <span
              v-if="scope.row.liquidato && scope.row.rif_fattura"
              class="c3__meta"
            >Rif. {{ scope.row.rif_fattura }}</span>
          </template>
        </el-table-column>
      </el-table>

      <!-- La barra compare solo dove ha senso: sulle già fatturate non c'è
           niente da fatturare. -->
      <div
        v-if="segment === 'dafatt'"
        class="bulk"
      >
        <span class="bulk__info">
          <b>{{ selected.length }}</b> selezionate · totale
          <b>{{ euro(selectedTotal) }}</b>
          <template v-if="selectedBenzina">
            + benzina <b>{{ euro(selectedBenzina) }}</b>
          </template>
        </span>
        <span class="c3__sp" />
        <el-input
          v-if="!tutti"
          v-model="rifFattura"
          placeholder="Rif. fattura"
          style="width: 170px"
        />
        <el-button
          :disabled="!selected.length"
          :loading="exporting"
          @click="exportPDF"
        >
          Scarica PDF
        </el-button>
        <el-button
          v-if="!tutti"
          type="success"
          :disabled="!selected.length"
          :loading="invoicing"
          @click="confirmInvoice"
        >
          Segna come fatturate
        </el-button>
        <span
          v-else
          class="bulk__info"
        >Per fatturare apri il COD3 del partner.</span>
      </div>

      <div class="board__foot">
        <span>{{ filtered.length }} {{ filtered.length === 1 ? 'riga' : 'righe' }}</span>
        <span class="c3__sp" />
        <span class="board__tot">
          Totale mostrato <b>{{ euro(filteredTotal) }}</b> imponibile
        </span>
      </div>
    </div>

    <!-- Benzina fatta al veicolo del partner: rimborso spese a parte. -->
    <el-dialog
      v-model="benzina.aperto"
      title="Benzina"
      width="420px"
    >
      <p class="c3__dlgRef">
        {{ benzina.riferimento }}
      </p>
      <p class="c3__dlgHint">
        Il rifornimento fatto dal driver al veicolo del partner. Si fattura a parte,
        senza IVA sopra (lo scontrino ha già la sua), e non cambia l'imponibile del
        servizio. Lo vede anche il partner. Vuoto = niente benzina.
      </p>
      <div class="c3__dlgField">
        <label for="c3-benz">Importo</label>
        <el-input
          id="c3-benz"
          v-model="benzina.importo"
          placeholder="0,00"
          inputmode="decimal"
        >
          <template #append>
            €
          </template>
        </el-input>
      </div>
      <template #footer>
        <el-button @click="benzina.aperto = false">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="benzina.salvando"
          @click="salvaBenzina"
        >
          Salva
        </el-button>
      </template>
    </el-dialog>

    <!-- Importi corretti a mano: vincono sul calcolo (anche sul pagato in
         app). Vuoti = vale il calcolo. -->
    <el-dialog
      v-model="importi.aperto"
      :title="importi.titolo"
      width="420px"
    >
      <p class="c3__dlgRef">
        {{ importi.riferimento }}
      </p>
      <p
        v-if="importi.corsa"
        class="c3__dlgHint"
      >
        Prezzo della tariffa: <b>{{ euro(importi.calcImp) }}</b> imponibile.
        Lascia vuoto per usare la tariffa. È l'imponibile della corsa: lo vede anche il partner, come costo.
        <template v-if="importi.giornataAMano">
          <br>La giornata è corretta a mano: salvando, la correzione della giornata si toglie e il totale
          torna la somma delle corse.
        </template>
      </p>
      <p
        v-else
        class="c3__dlgHint"
      >
        Calcolo: imponibile <b>{{ euro(importi.calcImp) }}</b>, totale <b>{{ euro(importi.calcTot) }}</b>.
        Lascia vuoto per usare il calcolo; il totale vuoto è l'imponibile più IVA.
      </p>
      <div class="c3__dlgField">
        <label for="c3-imp">Imponibile</label>
        <el-input
          id="c3-imp"
          v-model="importi.imponibile"
          :placeholder="String(importi.calcImp.toFixed(2))"
          inputmode="decimal"
        />
      </div>
      <div
        v-if="!importi.corsa"
        class="c3__dlgField"
      >
        <label for="c3-tot">Totale con IVA</label>
        <el-input
          id="c3-tot"
          v-model="importi.totale"
          :placeholder="importi.imponibile !== '' ? (Number(String(importi.imponibile).replace(',', '.')) * 1.22).toFixed(2) : String(importi.calcTot.toFixed(2))"
          inputmode="decimal"
        />
      </div>
      <template #footer>
        <el-button
          v-if="importi.manuale"
          @click="salvaImporti(true)"
        >
          {{ importi.corsa ? 'Torna alla tariffa' : 'Torna al calcolo' }}
        </el-button>
        <el-button @click="importi.aperto = false">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="importi.salvando"
          @click="salvaImporti(false)"
        >
          Salva
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import dayjs from 'dayjs';
import contabilitaAPI, { COD3_TUTTI, prezzoCorsa, totaleSpostamento } from '../../../services/contabilitaAPI.js';
import { periodFromRoute, syncPeriodToUrl } from '../../../services/period.js';
import partnerAPI from '../../../services/partnerAPI.js';
import bookingAPI from '../../../services/bookingAPI.js';
import StatusPill from '../../../components/ui/StatusPill.vue';
import ServiceChip from '../../../components/ui/ServiceChip.vue';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const SEGMENTS = [
  { key: 'dafatt', label: 'Da fatturare' },
  { key: 'fatt', label: 'Fatturate' },
];

// IVA 22%, come nel resto del dominio: le prenotazioni arrivano dal server già
// con imponibile e totale, gli spostamenti interni solo con l'imponibile.
const VAT = 1.22;
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export default {
  name: 'Cod3Page',
  components: { StatusPill, ServiceChip },

  setup() {
    const route = useRoute();
    const router = useRouter();
    // Il periodo arriva dalla URL (?y=&m=), non dal calendario: chi entra
    // dall'hub deve restare sul mese che stava guardando. Senza query valida
    // si ricade sul mese corrente.
    const periodo = periodFromRoute(route);
    const year = ref(periodo.year);
    const month = ref(periodo.month);

    const bookings = ref([]);
    const journeys = ref([]);
    const servizio = ref('tutti');
    const truncated = ref(false);
    const declaredTotal = ref(0);
    const loading = ref(false);
    const invoicing = ref(false);
    const search = ref('');
    const segment = ref('dafatt');
    const selected = ref([]);
    const rifFattura = ref('');
    const partnerName = ref('');
    const tableRef = ref(null);
    // Ricavi di tutti i partner insieme (/contabilita/cod3/tutti).
    const tutti = computed(() => route.params.id === COD3_TUTTI);
    const partnerDi = (r) =>
      (r.__journey ? r.partner?.company_name : r.headquarter?.partner?.company_name) || '';

    const periodLabel = computed(() => `${MONTHS[month.value - 1]} ${year.value}`);

    const customerName = (b) =>
      b.__journey
        ? 'Spostamento interno'
        : [b.customer?.name, b.customer?.surname].filter(Boolean).join(' ') || '—';

    // ---- Le corse di una giornata (riga aperta) ---------------------------
    // Le stesse che valgono qui per la targa prova: non le eliminate e non
    // contabilizzate. Nel totale entrano solo quelle confermate o concluse,
    // più le eliminate ma contabilizzate (regola di `cod3Journeys`): le altre
    // si vedono, con il loro prezzo, ma «fuori dal totale».
    const nelTotale = (t) =>
      ['CONFERMATA', 'CONCLUSA'].includes(t.status) || (t.status === 'ELIMINATA' && !!t.is_billed);
    const STATI_CORSA = {
      CONFERMATA: 'Confermata',
      CONCLUSA: 'Conclusa',
      ELIMINATA: 'Eliminata, contabilizzata',
    };
    const nomeDriver = (d) => (d ? [d.name, d.surname].filter(Boolean).join(' ') : '');
    const luogo = (p) =>
      p ? [p.address, p.street_number, p.city].filter(Boolean).join(' ').trim() : '';
    const ora = (d) => {
      const t = d ? new Date(d) : null;
      return t && !Number.isNaN(t.getTime())
        ? t.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
        : '';
    };
    const corseDi = (r) =>
      (r.routes || [])
        .filter((t) => t.status !== 'ELIMINATA' || t.is_billed)
        .sort((a, z) => new Date(a.dateStart || 0) - new Date(z.dateStart || 0))
        .map((t) => {
          const singolo = t.two_operators !== false;
          let jolly = '';
          if (singolo) {
            // Sul singolo il driver può rientrare coi mezzi (deciso da Simba):
            // allora il jolly non serve.
            jolly = t.rientro_altrove ? 'Rientro coi mezzi' : `Jolly: ${nomeDriver(t.jolly_driver) || '—'}`;
          }
          return {
            id: t.id,
            orario: [ora(t.dateStart), ora(t.dateEnd)].filter(Boolean).join('–'),
            da: luogo(t.startPoint),
            a: luogo(t.endPoint),
            veicolo: [t.marca, t.modello].filter(Boolean).join(' '),
            targa: t.targa || '',
            prova: t.immatricolato === false,
            driver: nomeDriver(t.driver),
            jolly,
            tipo: singolo ? 'Singolo' : 'A catena',
            km: t.km === null || t.km === undefined ? null : Number(t.km).toLocaleString('it-IT'),
            min: t.min === null || t.min === undefined ? null : Math.round(Number(t.min)),
            stato: STATI_CORSA[t.status] || 'Da confermare',
            // Corretto a mano da Simba, altrimenti il prezzo della tariffa.
            aMano: manuale(t.imponibile_manuale),
            prezzo: manuale(t.imponibile_manuale) || manuale(t.applied_price) ? prezzoCorsa(t) : null,
            benzina: Number(t.carburante || 0),
            nelTotale: nelTotale(t),
            route: t,
          };
        });

    // Il servizio vero di una riga. Per un NOTTURNO `type_prenotazione` resta
    // 'pickup' (è il tipo di corsa): quello che lo distingue è
    // `type_booking = 'restaurant'`, e finora la pagina non lo mostrava.
    // ⚠️ `type_booking` segue il PARTNER: anche il Transfer di un partner
    // notturno ce l'ha 'restaurant', e resta un Transfer.
    const servizioDi = (r) => {
      if (r.__journey) return 'journey';
      if (String(r.type_prenotazione || '').startsWith('escort')) return r.type_prenotazione;
      if (r.type_booking === 'restaurant') return 'restaurant';
      return r.type_prenotazione || '';
    };

    // Auto della riga. Sul booking è `car` (campi `marca`/`modello`/`targa`,
    // non brand/model/plate); sullo spostamento interno il veicolo sta sulle
    // TRATTE, e una giornata può muoverne più d'uno.
    const autoDi = (r) => {
      if (r.__journey) {
        // Solo le corse che valgono qui (le stesse del totale): una corsa
        // eliminata, o tolta con una modifica, non porta la sua auto nella
        // riga. Né i rientri del jolly (`movement: false`), che non muovono
        // nessuna auto.
        const valide = (r.routes || [])
          .filter((t) => t.movement !== false)
          .filter((t) => t.status !== 'ELIMINATA' || t.is_billed);
        const plates = [...new Set(valide.map((t) => t.targa).filter(Boolean))];
        const first = valide.find((t) => t.targa || t.marca || t.modello);
        if (!first) return null;
        const label = [first.marca, first.modello].filter(Boolean).join(' ') || 'Veicolo';
        const nProva = valide.filter((t) => t.immatricolato === false).length;
        let prova = '';
        if (nProva) {
          prova = nProva === valide.length
            ? 'Targa prova'
            : `Targa prova · ${nProva} ${nProva === 1 ? 'corsa' : 'corse'} su ${valide.length}`;
        }
        return {
          label: plates.length > 1 ? `${label} +${plates.length - 1}` : label,
          plate: plates.join(', '),
          prova,
        };
      }
      const c = r.car;
      if (!c) return null;
      return { label: [c.marca, c.modello].filter(Boolean).join(' ') || 'Auto', plate: c.targa || '' };
    };

    // Quanto il cliente ha già pagato in app su quella prenotazione, e quanto
    // resta da pagare: sono i `task` del booking (COD5), non il fatturato Simba.
    // ⚠️ `pay` = voce pagabile in app; `accepted` = pagata davvero.
    const inApp = (b) => (b.tasks ?? []).filter((t2) => t2.pay);
    const pagatoInApp = (b) =>
      inApp(b).reduce((s, t2) => s + (t2.accepted ? Number(t2.cost || 0) : 0), 0);
    const daPagareInApp = (b) =>
      inApp(b).reduce((s, t2) => s + (t2.accepted ? 0 : Number(t2.cost || 0)), 0);

    // Le righe della pagina: prenotazioni + spostamenti interni del mese.
    const righe = computed(() => [
      ...bookings.value,
      // Il prezzo applicato alle tratte è un IMPONIBILE: il totale con IVA si
      // calcola qui, come fa il server per il prezzo partner dello spostamento
      // (`JourneyPartnerPriceService`), così le due colonne dicono la stessa
      // cosa che dicono per le prenotazioni.
      ...journeys.value.map((j) => ({
        ...j,
        __journey: true,
        data: j.date,
        imponibile: j.totale,
        tot_fattura: round2(Number(j.totale || 0) * VAT),
        liquidato: !!j.liquidato,
      })),
    ]);

    const SERVIZI = [
      { value: 'tutti', label: 'Tutti i servizi' },
      { value: 'pickup', label: 'Pickup & Delivery' },
      { value: 'pickup_loaner', label: 'Pickup · auto cortesia' },
      { value: 'escort', label: 'Transfer' },
      { value: 'escort_loaner', label: 'Transfer · auto cortesia' },
      { value: 'restaurant', label: 'Notturno' },
      { value: 'journey', label: 'Spostamento interno' },
    ];
    // Nel menu restano «Tutti» e i servizi che il mese contiene davvero.
    const serviziDisponibili = computed(() => {
      const presenti = new Set(righe.value.map(servizioDi));
      return SERVIZI.filter((s) => s.value === 'tutti' || presenti.has(s.value));
    });

    // «Da fatturare» / «Fatturate»: prenotazioni e spostamenti allo stesso modo.
    const bySegment = (key) =>
      righe.value.filter((b) => (key === 'fatt' ? b.liquidato : !b.liquidato));

    const countOf = (key) => bySegment(key).length;

    const filtered = computed(() => {
      const q = search.value.trim().toLowerCase();
      return bySegment(segment.value).filter((b) => {
        if (servizio.value !== 'tutti' && servizioDi(b) !== servizio.value) return false;
        return !q ||
          `${partnerDi(b)} ${customerName(b)} ${autoDi(b)?.plate || ''}`.toLowerCase().includes(q);
      });
    });

    // -------------------------------------------------------------------
    //  QUANTO SI FATTURA DI UNA RIGA.
    //
    //  È sempre un IMPONIBILE: l'IVA la aggiunge la fattura, non questo
    //  resoconto. Due casi:
    //   - il cliente ha PAGATO IN APP (task accettati): quello che ha pagato
    //     è un importo con IVA, quindi si riporta al netto (÷ 1,22) e
    //     sostituisce l'imponibile della prenotazione — è quello l'incasso;
    //   - altrimenti vale `imponibile` della prenotazione.
    //  Sugli spostamenti interni l'imponibile è la somma dei prezzi applicati
    //  alle tratte, che il server manda già al netto.
    // -------------------------------------------------------------------
    // Il calcolo, prima delle correzioni a mano.
    const imponibileCalcolato = (r) => {
      if (r.__journey) return Number(r.imponibile || 0);
      const inApp = pagatoInApp(r);
      return inApp > 0 ? round2(inApp / VAT) : Number(r.imponibile || 0);
    };
    const manuale = (v) => v !== null && v !== undefined;
    // CORRETTO A MANO da Simba (sconti, accordi): vince sul calcolo, anche
    // sul pagato in app. Il totale scritto a mano vince; altrimenti, con
    // l'imponibile a mano, è quello più IVA.
    const fatturato = (r) => (manuale(r.imponibile_manuale) ? Number(r.imponibile_manuale) : imponibileCalcolato(r));
    const totaleDi = (r) => {
      if (manuale(r.totale_manuale)) return Number(r.totale_manuale);
      if (manuale(r.imponibile_manuale)) return round2(Number(r.imponibile_manuale) * VAT);
      return Number(r.tot_fattura || 0);
    };
    const aMano = (r) => manuale(r.imponibile_manuale) || manuale(r.totale_manuale);
    // BENZINA: rimborso spese a parte, senza IVA, fuori dall'imponibile.
    // Sugli spostamenti è la somma delle corse che valgono (totaleSpostamento).
    const benzinaDi = (r) => round2(Number(r.carburante || 0));
    // Quanto si fattura in tutto per la riga: il servizio con IVA più la benzina.
    const daFatturareDi = (r) => round2(totaleDi(r) + benzinaDi(r));

    const filteredTotal = computed(() =>
      filtered.value.reduce((s, b) => s + fatturato(b), 0)
    );

    const selectedTotal = computed(() =>
      selected.value.reduce((s, b) => s + fatturato(b), 0)
    );
    const selectedBenzina = computed(() =>
      round2(selected.value.reduce((s, b) => s + benzinaDi(b), 0))
    );

    const kpi = computed(() => {
      let prenotazioni = 0;
      let invoiced = 0;
      let toInvoice = 0;
      let inApp = 0;
      for (const b of bookings.value) {
        const v = fatturato(b);
        prenotazioni += v;
        if (b.liquidato) invoiced += v;
        else toInvoice += v;
        inApp += pagatoInApp(b);
      }
      const spostamentiRighe = righe.value.filter((r) => r.__journey);
      const spostamenti = spostamentiRighe.reduce((s, j) => s + fatturato(j), 0);
      for (const j of spostamentiRighe) {
        if (j.liquidato) invoiced += fatturato(j);
        else toInvoice += fatturato(j);
      }
      // Il fatturato del mese comprende gli SPOSTAMENTI INTERNI: sono ricavo
      // come le prenotazioni, solo che il loro documento non si fa da qui.
      // Per lo stesso motivo restano fuori da «da fatturare» e «già
      // fatturato», che seguono `liquidato` + `rif_fattura` del booking e
      // sulle tratte non esistono.
      const benzina = round2(righe.value.reduce((s, r) => s + benzinaDi(r), 0));
      return { total: round2(prenotazioni + spostamenti), invoiced, toInvoice, inApp, spostamenti, benzina };
    });

    const load = async () => {
      loading.value = true;
      selected.value = [];
      try {
        const res = await contabilitaAPI.cod3Rows(
          route.params.id, year.value, month.value
        );
        bookings.value = res.bookings;
        truncated.value = res.truncated;
        declaredTotal.value = res.total;
      } catch (e) {
        console.error('Caricamento COD3 fallito:', e);
        bookings.value = [];
        truncated.value = false;
      }
      // Gli spostamenti interni sono una sorgente a parte: se non rispondono,
      // le prenotazioni restano comunque leggibili.
      try {
        const res = await contabilitaAPI.cod3Journeys(
          route.params.id, year.value, month.value
        );
        journeys.value = res.journeys;
      } catch (e) {
        console.warn('Spostamenti interni non caricati:', e?.message);
        journeys.value = [];
      }
      loading.value = false;
    };

    const loadPartner = async () => {
      if (tutti.value) return;
      try {
        const r = await partnerAPI.getPartnerDetail(route.params.id);
        partnerName.value = r.data?.company_name || '';
      } catch (e) {
        console.warn('Nome partner non disponibile:', e?.message);
      }
    };

    const setSegment = (key) => {
      segment.value = key;
      selected.value = [];
      tableRef.value?.clearSelection?.();
    };

    const onSelectionChange = (rows) => { selected.value = rows; };

    // Fatturazione: si scrive `rif_fattura` e `liquidato: true` su ogni
    // prenotazione scelta. Sono PATCH separate, quindi si riporta l'esito
    // riga per riga invece di un generico "fatto".
    const confirmInvoice = async () => {
      if (!rifFattura.value.trim()) {
        ElMessage.warning('Indica il riferimento fattura');
        return;
      }
      try {
        await ElMessageBox.confirm(
          (selected.value.length === 1
            ? `1 riga per ${euro(selectedTotal.value)} verrà segnata come fatturata `
            : `${selected.value.length} righe per ${euro(selectedTotal.value)} verranno segnate come fatturate `) +
            'con il riferimento ' +
            `"${rifFattura.value}".`,
          'Confermi la fatturazione?',
          { confirmButtonText: 'Segna fatturate', cancelButtonText: 'Annulla' }
        );
      } catch {
        return;
      }

      invoicing.value = true;
      const res = await Promise.allSettled(
        selected.value.map((b) =>
          b.__journey
            ? contabilitaAPI.cod3FatturaSpostamento(b.id, { rif_fattura: rifFattura.value.trim(), liquidato: true })
            : bookingAPI.updateDateBooking(b.id, {
              rif_fattura: rifFattura.value,
              liquidato: true,
            })
        )
      );
      invoicing.value = false;

      const ko = res.filter((r) => r.status === 'rejected').length;
      if (ko) {
        ElMessage.error(
          `Fatturate ${res.length - ko} righe su ${res.length}: ${ko} non riuscite.`
        );
      } else {
        ElMessage.success(`${res.length} ${res.length === 1 ? 'riga segnata come fatturata' : 'righe segnate come fatturate'}`);
        rifFattura.value = '';
      }
      await load();
    };

    // ---- PDF delle righe scelte ------------------------------------------
    // Una riga per corsa: orario, partenza e arrivo, veicolo, km e minuti.
    // Data e servizio stanno in celle unite su tutte le corse della
    // prenotazione o della giornata. Gli importi (imponibile, totale fattura,
    // benzina e il totale fra fattura e benzina) sono di ogni corsa sugli
    // spostamenti e della prenotazione, uniti, sulle prenotazioni. Le tratte
    // delle prenotazioni la lista non le carica: si leggono adesso
    // (`cod3DettaglioPrenotazioni`).
    const exporting = ref(false);
    const luogoSede = (h) => (h ? [h.name, luogo(h)].filter(Boolean).join(' – ') : '');
    const corsePrenotazione = (b, det) => {
      const legs = [
        ...(det?.pickup_bookings || []).map((l) => ({ l, pickup: true })),
        ...(det?.escort_bookings || []).map((l) => ({ l, pickup: false })),
      ];
      return legs
        .map(({ l, pickup }) => {
          const andata = l.type_booking !== 'return';
          // Pickup: casa del cliente ↔ sede (all'andata si parte da casa). Il
          // Transfer ha i suoi due capi.
          const [da, a] = pickup
            ? (andata ? [luogo(l.point), luogoSede(b.headquarter)] : [luogoSede(b.headquarter), luogo(l.point)])
            : [luogo(l.point_origin_escort), luogo(l.point_destination_escort)];
          const inizio = andata ? l.date_customer || l.date_partner : l.date_partner || l.date_customer;
          const fine = andata ? l.date_partner : l.date_customer;
          const dopo = fine && new Date(fine) > new Date(inizio);
          return {
            inizio,
            orario: [ora(inizio), dopo ? ora(fine) : ''].filter(Boolean).join('–'),
            tipo: andata ? 'Andata' : 'Ritorno',
            veicolo: '',
            da,
            a,
            km: l.simba_km === null || l.simba_km === undefined ? null : Number(l.simba_km),
            min: l.simba_minute === null || l.simba_minute === undefined ? null : Math.round(Number(l.simba_minute)),
          };
        })
        .sort((x, z) => new Date(x.inizio || 0) - new Date(z.inizio || 0));
    };
    const exportPDF = async () => {
      exporting.value = true;
      try {
        const scelte = [...selected.value].sort((x, z) => new Date(x.data || 0) - new Date(z.data || 0));
        // Senza il dettaglio delle prenotazioni il PDF non si fa (mancherebbero
        // le corse); senza i nomi delle sedi sì, con il solo indirizzo.
        const [dettagli, sedi] = await Promise.all([
          contabilitaAPI.cod3DettaglioPrenotazioni(scelte.filter((r) => !r.__journey).map((r) => r.id)),
          contabilitaAPI.cod3NomiSedi(scelte.filter((r) => r.__journey)
            .flatMap((r) => (r.routes || []).flatMap((t) => [t.start_headquarter_id, t.end_headquarter_id])))
            .catch((e) => {
              console.warn('Nomi delle sedi non caricati:', e?.message);
              return new Map();
            }),
        ]);
        // Senza «€»: il carattere standard del PDF non lo ha.
        const n = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toFixed(2).replace('.', ','));
        const servizioLbl = (r) => SERVIZI.find((x) => x.value === servizioDi(r))?.label || '';
        // Il capo di una corsa di spostamento: il nome della sede, se lo è,
        // con l'indirizzo.
        const capo = (p, sedeId) => [sedeId ? sedi.get(sedeId) : '', luogo(p)].filter(Boolean).join(' – ');

        // UNA RIGA PER CORSA. I dati della prenotazione o della giornata
        // (data, servizio) stanno in celle unite su tutte
        // le sue corse; le righe di una stessa prenotazione hanno lo stesso
        // sfondo, alternato fra una prenotazione e l'altra.
        const body = [];
        const somme = { imp: 0, fatt: 0, benz: 0, tot: 0 };
        scelte.forEach((r, i) => {
          const sfondo = i % 2 ? [244, 246, 251] : [255, 255, 255];
          const auto = autoDi(r);
          const veicoloPrenotazione = r.__journey ? '' : [
            [auto?.label, auto?.plate].filter(Boolean).join(' · '),
            r.loaner ? 'auto di cortesia' : '',
          ].filter(Boolean).join('\n');
          const corse = r.__journey
            ? corseDi(r).map((c) => ({
              ...c,
              da: capo(c.route.startPoint, c.route.start_headquarter_id),
              a: capo(c.route.endPoint, c.route.end_headquarter_id),
              veicolo: [[c.veicolo, c.targa].filter(Boolean).join(' · '), c.prova ? 'targa prova' : '']
                .filter(Boolean).join('\n'),
              km: c.route.km === null || c.route.km === undefined ? null : Number(c.route.km),
            }))
            : corsePrenotazione(r, dettagli.get(r.id)).map((c) => ({ ...c, veicolo: veicoloPrenotazione }));
          const righeCorse = corse.length ? corse : [{}];
          // Con tutti i partner, il partner sopra il servizio.
          const servizio = [tutti.value ? partnerDi(r) : '', servizioLbl(r)].filter(Boolean).join('\n');
          const unita = (content, extra = {}) => ({ content, rowSpan: righeCorse.length, styles: { fillColor: sfondo, ...extra } });
          const cella = (content, extra = {}) => ({ content, styles: { fillColor: sfondo, ...extra } });
          // Gli IMPORTI: sugli spostamenti sono di ogni corsa (imponibile,
          // totale con IVA, benzina e totale), sulle prenotazioni della
          // prenotazione, uniti sulle sue tratte. Una giornata corretta a mano
          // nel COD3 ha un importo solo: resta unito sulla giornata.
          const perCorsa = r.__journey && !aMano(r);
          const importi = (imp, fatt, benz, unisci) => {
            somme.imp += imp;
            somme.fatt += fatt;
            somme.benz += benz;
            somme.tot += round2(fatt + benz);
            const f = unisci ? unita : cella;
            return [
              f(n(imp), { fontStyle: 'bold' }),
              f(n(fatt)),
              f(benz ? n(benz) : ''),
              f(n(round2(fatt + benz)), { fontStyle: 'bold' }),
            ];
          };
          righeCorse.forEach((c, k) => {
            const corsa = [
              [c.orario, c.tipo].filter(Boolean).join('\n'),
              c.da || '', c.a || '', c.veicolo || '',
              c.km === null || c.km === undefined ? '' : n(c.km), c.min ?? '',
            ].map((content) => cella(content));
            let soldi = [];
            if (perCorsa) {
              // Una corsa ancora da confermare non entra nel totale, come
              // nella pagina.
              if (!c.nelTotale) {
                soldi = [{
                  ...cella('da confermare, non conteggiata', { halign: 'center', textColor: [120, 126, 145], fontStyle: 'italic' }),
                  colSpan: 4,
                }];
              } else {
                const imp = round2(prezzoCorsa(c.route));
                soldi = importi(imp, round2(imp * VAT), round2(Number(c.route.carburante || 0)), false);
              }
            } else if (k === 0) {
              soldi = importi(fatturato(r), totaleDi(r), benzinaDi(r), true);
            }
            body.push(k === 0
              ? [
                unita([fmtDate(r.data), fmtTime(r)].filter(Boolean).join('\n'), { fontStyle: 'bold' }),
                unita(servizio),
                ...corsa,
                ...soldi,
              ]
              : [...corsa, ...soldi]);
          });
        });
        // I totali sono la somma di quello che il PDF mostra riga per riga.
        const piede = { fontStyle: 'bold', fillColor: [222, 226, 240] };
        body.push([
          { content: `Totale · ${scelte.length} ${scelte.length === 1 ? 'prenotazione o giornata' : 'prenotazioni e giornate'}`, colSpan: 8, styles: piede },
          { content: n(round2(somme.imp)), styles: piede },
          { content: n(round2(somme.fatt)), styles: piede },
          { content: n(round2(somme.benz)), styles: piede },
          { content: n(round2(somme.tot)), styles: piede },
        ]);

        const pdf = new jsPDF({ orientation: 'landscape' });
        pdf.setFontSize(13);
        pdf.text(`COD3 · ${tutti.value ? 'Tutti i partner' : (partnerName.value || 'Partner')} · ${periodLabel.value}`, 8, 12);
        pdf.setFontSize(7.5);
        pdf.text(
          `${rifFattura.value.trim() ? `Rif. fattura ${rifFattura.value.trim()} · ` : ''}Generato il ${dayjs().format('DD/MM/YYYY')}. `
            + 'Tot. fattura = imponibile + IVA 22%; la benzina è senza IVA; Totale = tot. fattura + benzina.',
          8, 17,
        );
        const destra = { halign: 'right' };
        pdf.autoTable({
          head: [['Data', 'Servizio', 'Orario', 'Partenza', 'Arrivo', 'Veicolo', 'Km', 'Min', 'Imponibile', 'Tot. fattura', 'Benzina', 'Totale']],
          body,
          startY: 20,
          margin: { left: 8, right: 8, bottom: 10 },
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 1.3, valign: 'top', overflow: 'linebreak', lineColor: [214, 218, 230], lineWidth: 0.15 },
          headStyles: { fillColor: [35, 44, 81], textColor: 255, fontSize: 7 },
          columnStyles: {
            0: { cellWidth: 18 },
            1: { cellWidth: 26 },
            2: { cellWidth: 20 },
            3: { cellWidth: 47 },
            4: { cellWidth: 47 },
            5: { cellWidth: 30 },
            6: { cellWidth: 13, ...destra },
            7: { cellWidth: 11, ...destra },
            8: { cellWidth: 18, ...destra },
            9: { cellWidth: 18, ...destra },
            10: { cellWidth: 16, ...destra },
            11: { cellWidth: 17, ...destra },
          },
          didDrawPage: () => {
            pdf.setFontSize(7);
            pdf.text(`Pagina ${pdf.internal.getNumberOfPages()}`, pdf.internal.pageSize.getWidth() - 22, pdf.internal.pageSize.getHeight() - 5);
          },
        });
        pdf.save(`cod3-${tutti.value ? 'tutti' : (partnerName.value || 'partner').replace(/[^\p{L}\p{N}]+/gu, '-')}-${year.value}-${String(month.value).padStart(2, '0')}.pdf`);
      } catch (e) {
        console.error('PDF non generato:', e);
        ElMessage.error('PDF non generato: riprova.');
      } finally {
        exporting.value = false;
      }
    };

    // ---- Importi corretti a mano -----------------------------------------
    const importi = ref({
      aperto: false, salvando: false, riga: null, corsa: null, manuale: false, giornataAMano: false,
      titolo: '', imponibile: '', totale: '', calcImp: 0, calcTot: 0,
    });
    const testoImporto = (v) => (v === null || v === undefined ? '' : String(Number(v).toFixed(2)));
    const apriImporti = (r) => {
      importi.value = {
        aperto: true,
        salvando: false,
        riga: r,
        corsa: null,
        manuale: aMano(r),
        giornataAMano: false,
        titolo: 'Correggi gli importi',
        riferimento: `${customerName(r)} · ${fmtDate(r.data)}`,
        imponibile: testoImporto(r.imponibile_manuale),
        totale: testoImporto(r.totale_manuale),
        calcImp: imponibileCalcolato(r),
        calcTot: r.__journey ? round2(imponibileCalcolato(r) * VAT) : Number(r.tot_fattura || 0),
      };
    };
    // L'imponibile di una corsa della giornata: solo l'imponibile, il totale
    // della giornata si ricalcola dalla somma delle corse.
    const apriImportoCorsa = (r, c) => {
      importi.value = {
        aperto: true,
        salvando: false,
        riga: r,
        corsa: c.route,
        manuale: c.aMano,
        giornataAMano: aMano(r),
        titolo: "Correggi l'importo della corsa",
        riferimento: `${fmtDate(r.data)} · ${c.orario} · ${c.da || '—'} → ${c.a || '—'}`,
        imponibile: testoImporto(c.route.imponibile_manuale),
        totale: '',
        calcImp: Number(c.route.applied_price || 0),
        calcTot: 0,
      };
    };
    // ---- Benzina ----------------------------------------------------------
    // Sulla prenotazione, o sulla singola corsa di uno spostamento.
    const benzina = ref({ aperto: false, salvando: false, riga: null, corsa: null, riferimento: '', importo: '' });
    const apriBenzina = (r, c) => {
      const attuale = c ? c.benzina : benzinaDi(r);
      benzina.value = {
        aperto: true,
        salvando: false,
        riga: r,
        corsa: c ? c.route : null,
        riferimento: c
          ? `${fmtDate(r.data)} · ${c.orario} · ${[c.veicolo, c.targa].filter(Boolean).join(' ') || 'corsa'}`
          : `${customerName(r)} · ${fmtDate(r.data)}`,
        importo: attuale ? attuale.toFixed(2).replace('.', ',') : '',
      };
    };
    const salvaBenzina = async () => {
      const b = benzina.value;
      const t = String(b.importo ?? '').trim().replace(',', '.');
      const importo = t === '' ? null : Number(t);
      if (importo !== null && (!Number.isFinite(importo) || importo < 0)) {
        ElMessage.error("L'importo deve essere un numero non negativo (es. 45,50)");
        return;
      }
      const valore = importo === null || importo === 0 ? null : round2(importo);
      b.salvando = true;
      try {
        if (b.corsa) {
          await contabilitaAPI.cod3SetCarburante('route', b.corsa.id, valore);
          const fonte = journeys.value.find((x) => x.id === b.riga.id);
          const t2 = (fonte?.routes || []).find((x) => x.id === b.corsa.id);
          if (t2) t2.carburante = valore;
          if (fonte) Object.assign(fonte, totaleSpostamento(fonte));
        } else {
          await contabilitaAPI.cod3SetCarburante('booking', b.riga.id, valore);
          const fonte = bookings.value.find((x) => x.id === b.riga.id);
          if (fonte) fonte.carburante = valore;
        }
        b.aperto = false;
        ElMessage.success(valore === null ? 'Benzina tolta' : 'Benzina salvata');
      } catch (e) {
        ElMessage.error(e?.response?.data?.message || 'Benzina non salvata');
      } finally {
        b.salvando = false;
      }
    };

    const salvaImportoCorsa = async (r, corsa, imponibile) => {
      await contabilitaAPI.cod3SetImportoCorsa(corsa.id, imponibile);
      // Si aggiorna la corsa nella giornata di partenza e se ne rifà il totale.
      const fonte = journeys.value.find((x) => x.id === r.id);
      const t = (fonte?.routes || []).find((x) => x.id === corsa.id);
      if (t) t.imponibile_manuale = imponibile;
      // Il server toglie gli importi a mano della giornata: il totale torna
      // la somma delle corse.
      if (fonte) Object.assign(fonte, { imponibile_manuale: null, totale_manuale: null });
      if (fonte) Object.assign(fonte, totaleSpostamento(fonte));
    };
    const salvaImporti = async (alCalcolo) => {
      const r = importi.value.riga;
      if (!r) return;
      const leggi = (v) => {
        const t = String(v ?? '').trim().replace(',', '.');
        if (!t) return null;
        const n = Number(t);
        return Number.isFinite(n) && n >= 0 ? round2(n) : NaN;
      };
      const imponibile = alCalcolo ? null : leggi(importi.value.imponibile);
      const totale = alCalcolo ? null : leggi(importi.value.totale);
      if (Number.isNaN(imponibile) || Number.isNaN(totale)) {
        ElMessage.error('Gli importi devono essere numeri, non negativi (es. 120,50)');
        return;
      }
      importi.value.salvando = true;
      try {
        if (importi.value.corsa) {
          await salvaImportoCorsa(r, importi.value.corsa, imponibile);
        } else {
          await contabilitaAPI.cod3SetImporti(r.__journey ? 'journey' : 'booking', r.id, { imponibile, totale });
          // Si aggiorna la riga di partenza (prenotazione o spostamento).
          const fonte = (r.__journey ? journeys.value : bookings.value).find((x) => x.id === r.id);
          if (fonte) Object.assign(fonte, { imponibile_manuale: imponibile, totale_manuale: totale });
        }
        importi.value.aperto = false;
        if (importi.value.corsa) {
          ElMessage.success(alCalcolo ? 'La corsa torna alla tariffa' : 'Importo della corsa salvato');
        } else {
          ElMessage.success(alCalcolo ? 'Importi tornati al calcolo' : 'Importi salvati');
        }
      } catch (e) {
        ElMessage.error(e?.response?.data?.message || 'Importi non salvati');
      } finally {
        importi.value.salvando = false;
      }
    };

    const shiftMonth = (delta) => {
      let m = month.value + delta;
      let y = year.value;
      if (m < 1) { m = 12; y -= 1; }
      if (m > 12) { m = 1; y += 1; }
      month.value = m;
      year.value = y;
      // la URL segue il mese mostrato: un refresh non riporta a oggi
      syncPeriodToUrl(router, route, y, m);
      load();
    };

    function euro(n) {
      return `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
    }

    function fmtDate(d) {
      return d
        ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
        : '—';
    }

    // Ora della prenotazione, se c'è. La mezzanotte esatta è quasi sempre una
    // data senza orario, non una corsa alle 00:00: non si mostra.
    function fmtTime(row) {
      if (row.__journey || !row.data) return '';
      const d = new Date(row.data);
      if (Number.isNaN(d.getTime()) || (!d.getHours() && !d.getMinutes())) return '';
      return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    }

    const goBack = () => router.push('/contabilita');

    onMounted(() => { load(); loadPartner(); });

    return {
      corseDi,
      year, month, bookings, journeys, loading, invoicing, search, segment, selected,
      rifFattura, partnerName, truncated, declaredTotal, tableRef, tutti, partnerDi,
      filtered, filteredTotal, selectedTotal, fatturato, kpi, periodLabel, SEGMENTS,
      servizio, serviziDisponibili, servizioDi, autoDi, pagatoInApp, daPagareInApp, fmtTime,
      countOf, setSegment, onSelectionChange, confirmInvoice, exportPDF, exporting,
      shiftMonth, euro, fmtDate, customerName, goBack,
      totaleDi, aMano, importi, apriImporti, apriImportoCorsa, salvaImporti,
      benzina, benzinaDi, daFatturareDi, selectedBenzina, apriBenzina, salvaBenzina,
    };
  },
};
</script>

<style scoped>
/* Importi corretti a mano */
.c3__edit {
  white-space: nowrap;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 12.5px;
  color: var(--brand-violet, #6d4fe0);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}
.c3__edit:focus-visible { outline: 2px solid var(--brand-violet, #6d4fe0); outline-offset: 2px; }
.c3__manuale { color: var(--brand-violet, #6d4fe0); font-weight: 600; }
.c3__dlgRef { margin: 0 0 6px; font-weight: 600; word-break: normal; }
.c3__dlgHint { word-break: normal; overflow-wrap: break-word; margin: 0 0 14px; font-size: 13px; color: var(--text-secondary, #64748b); line-height: 1.5; }
.c3__dlgField { display: grid; gap: 4px; margin-bottom: 12px; }
.c3__dlgField label { font-size: 12px; font-weight: 600; color: var(--text-secondary, #64748b); }
.c3 { padding: 4px 0 32px; }
.c3__sp { flex: 1; }

.c3__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 2px 18px;
}
.c3__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.c3__back:hover { border-color: var(--brand-violet); }
.c3__title {
  margin: 0; font-size: 21px; display: flex; align-items: center; gap: 10px;
  color: var(--text-primary);
}
.c3__code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; font-weight: 700; color: #fff; background: var(--state-ok);
  padding: 3px 8px; border-radius: 6px;
}
.c3__sub { margin: 3px 0 0; font-size: 13px; color: var(--text-tertiary); }

.c3__filters {
  display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 14px;
}
.field {
  display: flex; align-items: center; gap: 8px; padding: 5px 11px;
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: 10px;
}
.field label {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 700;
}
.period { display: flex; align-items: center; gap: 2px; }
.period__nav {
  width: 30px; height: 30px; border: 0; background: transparent;
  border-radius: 7px; cursor: pointer; font-size: 15px;
  color: var(--text-secondary); font-family: inherit;
}
.period__nav:hover { background: var(--bg-tertiary); }
.period__lbl {
  padding: 0 8px; font-weight: 700; font-variant-numeric: tabular-nums;
  min-width: 118px; text-align: center; color: var(--text-primary);
}

.warnbox {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 13px; margin-bottom: 14px;
  border-radius: 9px; font-size: 12.5px; line-height: 1.5;
  color: var(--state-attn); background: var(--state-attn-bg);
  border: 1px solid var(--state-attn-line);
}
.warnbox__ico { flex: none; font-size: 14px; line-height: 1.35; }
.warnbox__txt { flex: 1; }

.kpis {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px;
}
@media (max-width: 820px) { .kpis { grid-template-columns: repeat(2, 1fr); } }
.kpi {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); padding: 13px 15px;
}
.kpi__lbl {
  font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 600;
}
.kpi__num {
  font-size: 22px; font-weight: 750; letter-spacing: -0.02em; margin-top: 4px;
  font-variant-numeric: tabular-nums; color: var(--text-primary);
}
.kpi--attn .kpi__num { color: var(--state-attn); }

.board {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.toolbar {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-bottom: 1px solid var(--border-color);
}
.segs { display: flex; gap: 5px; }
.seg {
  border: 1px solid transparent; background: transparent; cursor: pointer;
  color: var(--text-secondary); padding: 7px 12px; border-radius: 8px;
  font-size: 13px; font-weight: 600; font-family: inherit;
  display: inline-flex; align-items: center; gap: 8px;
}
.seg:hover { background: var(--bg-tertiary); }
.seg.is-active {
  background: var(--svc-fleet-bg); color: var(--brand-violet);
  border-color: var(--brand-violet);
}
.seg__c {
  font-size: 11.5px; font-weight: 700; border-radius: 20px; padding: 1px 8px;
  background: var(--state-muted-bg); color: var(--state-muted);
}
.seg.is-active .seg__c { background: var(--brand-violet); color: #fff; }

.bulk {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 16px; border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
}
.bulk__info { font-size: 12.5px; color: var(--text-secondary); }
.bulk__info b { font-variant-numeric: tabular-nums; color: var(--text-primary); }

.cli__nm { font-weight: 600; color: var(--text-primary); }
/* le celle di el-table spezzano le parole a metà: il nome del partner no */
.c3__partner { word-break: normal; overflow-wrap: break-word; }
/* Singolo o a catena di una corsa: stessi colori del servizio. */
.cod3__tipo {
  margin-left: 6px; font-size: 10.5px; font-weight: 700; padding: 1px 7px; border-radius: 5px;
  white-space: nowrap; color: var(--svc-fleet); background: var(--svc-fleet-bg);
}
.cli__sub { color: var(--text-tertiary); font-size: 11.5px; }
/* Stessa pastiglia del dettaglio spostamento: condizione del veicolo che
   cambia la tariffa, colore d'attenzione. */
.c3__tagImm {
  display: inline-block; margin-left: 6px; font-size: 11px; font-weight: 700;
  border-radius: 5px; padding: 1px 7px; color: #92400e; background: #fef3c7;
  white-space: nowrap;
}
.num { font-variant-numeric: tabular-nums; }
.val { font-weight: 700; color: var(--text-primary); }
.muted { color: var(--text-tertiary); }
.c3__meta {
  display: block; margin-top: 3px; font-size: 11.5px; color: var(--text-tertiary);
}

.board__foot {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-top: 1px solid var(--border-color);
  font-size: 12.5px; color: var(--text-tertiary);
}
.board__tot { color: var(--text-secondary); }
.board__tot b {
  color: var(--text-primary); font-variant-numeric: tabular-nums; font-size: 14px;
}

/* Le prenotazioni non si aprono: niente freccia. */
.c3 :deep(.c3-row--noexp .el-table__expand-icon) { display: none; }

/* Corse di una giornata (riga aperta). La tabella è più larga del riquadro
   e scorre di lato: il dettaglio resta fermo e largo quanto il riquadro
   (`cqw` del contenitore .board), così il prezzo non finisce fuori vista. */
.board { container-type: inline-size; }
.c3 :deep(.el-table__expanded-cell) { padding: 0 !important; }
.c3x {
  position: sticky; left: 0; box-sizing: border-box; width: 100cqw;
  padding: 4px 16px 12px 52px; background: var(--bg-secondary);
}
.c3x__list { list-style: none; margin: 0; padding: 0; }
.c3x__corsa {
  display: grid; align-items: start; gap: 4px 16px;
  grid-template-columns: 96px minmax(220px, 2fr) minmax(170px, 1.2fr) 250px;
  padding: 9px 10px; border-bottom: 1px solid var(--border-color); font-size: 12.5px;
  color: var(--text-primary);
}
.c3x__corsa:last-child { border-bottom: 0; }
.c3x__ora { font-weight: 600; white-space: nowrap; }
.c3x__percorso { overflow-wrap: anywhere; }
.c3x__chi .cod3__tipo { margin: 0 6px 0 0; }
.c3x__importi, .c3x__foot {
  display: grid; grid-template-columns: 1fr auto 64px; align-items: baseline;
  gap: 3px 12px; white-space: nowrap;
}
.c3x__importi .num { text-align: right; color: var(--text-primary); }
.c3x__importi .c3__edit { justify-self: start; }
.c3x__importi.is-fuori .num { color: var(--text-tertiary); }
.c3x__voce { color: var(--text-secondary); }
.c3x__voce--tot, .c3x__tot { font-weight: 600; color: var(--text-primary); }
.c3x__voce--tot, .c3x__tot { padding-top: 3px; border-top: 1px solid var(--border-color); }
.c3x__fuori { grid-column: 1 / -1; font-size: 11.5px; color: var(--text-tertiary); white-space: normal; }
.c3__auto { margin-top: 2px; }
.c3__targa { font-variant-numeric: tabular-nums; letter-spacing: 0.03em; }
.c3__edit--block { display: block; margin: 2px 0 0 auto; }
@media (max-width: 900px) {
  .c3x { padding-left: 16px; }
  .c3x__corsa { grid-template-columns: 1fr; }
  .c3x__importi, .c3x__foot { width: 100%; max-width: 320px; }
}
.c3x__foot {
  width: 250px; margin: 8px 10px 0 auto; padding-top: 8px; font-size: 12.5px;
  border-top: 2px solid var(--border-color);
}
.c3x__foot b { color: var(--text-primary); text-align: right; }
.c3x__foot .c3x__voce { grid-column: 1; }
.c3x__foot b { grid-column: 2; }
.c3x__nota { grid-column: 1 / -1; margin: 4px 0 0; white-space: normal; color: var(--text-secondary); }

.c3 :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  padding: 10px 12px; border-bottom: 1px solid var(--border-color);
}
</style>
